import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

import { Tooltip } from 'antd';

import styled from 'styled-components';

import StyleVariables from '@components/Gui/StyleVariables.js';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const BadgeSpan = styled.span`
    .ant-badge-count {
        background-color: ${(props) => props.theme.PrimaryColor};
        font-size: 0.7rem;
    }
    sub.ant-badge-count {
        bottom: -0.5rem;
        right: -0.25rem;
        top: auto;
    }
`;

const StyledSpan = styled.span`
    position: relative;
    .bool-true {
        .fa-primary {
            fill: #fff;
            opacity: 1;
        }
        .fa-secondary {
            fill: ${(props) => props.theme.SuccessColor};
            opacity: 1;
        }
    }
    .bool-false {
        .fa-primary {
            fill: #fff;
            opacity: 1;
        }
        .fa-secondary {
            fill: ${(props) => props.theme.DangerColor};
            opacity: 1;
        }
    }
`;

export const NwBoolIcon = ({ value = true, fontSize = '1em' }) => {
    if (value) {
        return (
            <StyledSpan>
                <NwBaseIcon icon={byPrefixAndName.fass['check']} style={{ fontSize }} className="bool-true" />
            </StyledSpan>
        );
    }
    return (
        <StyledSpan>
            <NwBaseIcon icon={byPrefixAndName.fass['ban']} style={{ fontSize }} className="bool-false" />
        </StyledSpan>
    );
};

export const NwGenderIcon = ({ gender }) => {
    const genderIcons = {
        Female: byPrefixAndName.fasl['venus'],
        Male: byPrefixAndName.fasl['mars'],
        NonBinary: byPrefixAndName.fasl['mars-and-venus'],
    };

    //check if gender can be converted to a number and convert it to string
    let genderString = 'Female';
    if (!isNaN(gender)) {
        switch (Number(gender)) {
            case 1:
                genderString = 'Female';
                break;
            case 2:
                genderString = 'Male';
                break;
            case 3:
                genderString = 'NonBinary';
                break;
            default:
                break;
        }
    } else {
        genderString = gender;
    }

    return (
        <StyledSpan>
            <NwBaseIcon icon={genderIcons[genderString]} />
        </StyledSpan>
    );
};

const getFaIcon = (icon) => {
    if (!icon) {
        return byPrefixAndName.fass['martini-glass-empty'];
    }
    return icon;
};

const NwBaseIcon = (props) => {
    const {
        icon,
        loading,
        primary,
        danger,
        success,
        color,
        pointer,
        size,
        disabled,
        opacity,
        style,
        spin,
        onClick,
        ...other
    } = props;

    const mystyle = style || {};
    if (pointer) {
        mystyle.cursor = 'pointer';
    }
    if (opacity) {
        mystyle.opacity = opacity;
    }
    let iconcolor = color;
    if (danger) {
        iconcolor = StyleVariables.DangerColor;
    }
    if (success) {
        iconcolor = StyleVariables.SuccessColor;
    }
    if (primary) {
        iconcolor = StyleVariables.PrimaryColor;
    }
    if (disabled) {
        mystyle.opacity = '.4';
        mystyle.cursor = 'not-allowed';
        iconcolor = StyleVariables.TextColorMuted;
    }

    let icontouse = getFaIcon(icon);
    let spinicon = spin;
    if (loading) {
        icontouse = byPrefixAndName.fasds['spinner-third'];
        spinicon = true;
    }

    return (
        <FontAwesomeIcon
            onClick={disabled ? null : onClick}
            icon={icontouse}
            size={size || '1x'}
            color={iconcolor}
            spin={spinicon}
            style={mystyle}
            {...other}
        />
    );
};

const NwIconBadgeWrapper = ({ children, badgeContent, badgePosition = 'top-right' }) => {
    return (
        <BadgeSpan className="ant-badge" badgePosition={badgePosition}>
            {children}
            {badgePosition === 'bottom-right' && (
                <sub data-show="true" className="ant-scroll-number ant-badge-count ant-badge-count-sm">
                    <span className="ant-scroll-number-only" style={{ transition: 'none' }}>
                        <span className="ant-scroll-number-only-unit current">{badgeContent}</span>
                    </span>
                </sub>
            )}
            {badgePosition === 'top-right' && (
                <sup data-show="true" className="ant-scroll-number ant-badge-count ant-badge-count-sm">
                    <span className="ant-scroll-number-only" style={{ transition: 'none' }}>
                        <span className="ant-scroll-number-only-unit current">{badgeContent}</span>
                    </span>
                </sup>
            )}
        </BadgeSpan>
    );
};

const NwIcon = ({
    icon,
    badge,
    badgePosition,
    plus,
    minus,
    edit,
    check,
    close,
    pointer,
    size,
    tooltip,
    loading,
    ...other
}) => {
    if (plus) {
        return (
            <NwIconBadgeWrapper badgeContent="+">
                <NwBaseIcon loading={loading} title={tooltip} icon={icon} {...other} pointer size={size || 'lg'} />
            </NwIconBadgeWrapper>
        );
    }
    if (minus) {
        return (
            <NwIconBadgeWrapper badgeContent="-">
                <NwBaseIcon loading={loading} title={tooltip} icon={icon} {...other} pointer size={size || 'lg'} />
            </NwIconBadgeWrapper>
        );
    }
    if (edit) {
        return (
            <NwIconBadgeWrapper badgeContent={<NwIcon size="xs" icon={byPrefixAndName.fass['pen']} pointer />}>
                <NwBaseIcon loading={loading} title={tooltip} icon={icon} {...other} pointer size={size || 'lg'} />
            </NwIconBadgeWrapper>
        );
    }
    if (check) {
        return (
            <NwIconBadgeWrapper badgeContent="✓">
                <NwBaseIcon loading={loading} title={tooltip} icon={icon} {...other} pointer size={size || 'lg'} />
            </NwIconBadgeWrapper>
        );
    }
    if (badge) {
        return (
            <NwIconBadgeWrapper badgeContent={badge} badgePosition={badgePosition}>
                <NwBaseIcon loading={loading} title={tooltip} icon={icon} {...other} pointer size={size || 'lg'} />
            </NwIconBadgeWrapper>
        );
    }
    return tooltip ? (
        <Tooltip zIndex={9999} title={tooltip} mouseEnterDelay={0.4} mouseLeaveDelay={0.2}>
            <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
                <NwBaseIcon loading={loading} icon={icon} {...other} pointer={pointer} size={size || '1x'} />
            </div>
        </Tooltip>
    ) : (
        <NwBaseIcon loading={loading} icon={icon} {...other} pointer={pointer} size={size || '1x'} />
    );
};

export default NwIcon;
